// components/EmployeeCalendar/EmployeeCalendarDialog.tsx
import { useEffect, useRef } from "react";
import styles from "./EmployeeCalendarDialog.module.scss";
import { IEmployeeWithAvailabilityModel } from "../models/employeeCalendarModels";
import { useEmployeeCalendar } from "../hooks/useEmployeeCalendarDialog";
import { CalendarPanel } from "./CalendarPanel";
import { SlotsPanel } from "./SlotsPanel";
import { SummaryFooter } from "./SummaryFooter";
import { EmployeeChipStrip } from "./EmployeeChipStrip";

const MOBILE_LAYOUT_QUERY = "(max-width: 900px)";

interface IEmployeeCalendarDialogProps {
  employeeFieldName: string;
  dateFieldName: string;
  durationFieldName: string;
  employeesData: IEmployeeWithAvailabilityModel[];
  selectedEmployeeId?: number;
  selectedDate?: Date;
  selectedDuration?: number;
  bufferAfterMinutes?: number;
  onClose: () => void;
}

export const EmployeeCalendarDialog = ({
  employeeFieldName,
  dateFieldName,
  durationFieldName,
  employeesData,
  selectedEmployeeId,
  selectedDate,
  selectedDuration,
  bufferAfterMinutes,
  onClose,
}: IEmployeeCalendarDialogProps) => {
  const {
    currentEmployee,
    selectedDateState,
    selectedEmployee,
    calendarDays,
    timeSlots,
    groupedSlots,
    selectedDateText,
    monthYearText,
    canNavigatePrev,

    selectionMode,
    customDateTime,
    customConflictWarning,
    openCustomMode,
    closeCustomMode,
    handleCustomDateTimeChange,

    summary,
    canConfirm,
    handleConfirm,

    handleEmployeeChange,
    handleDateSelect,
    handleTimeSlotSelect,
    navigateMonth,
  } = useEmployeeCalendar({
    employeeFieldName,
    dateFieldName,
    durationFieldName,
    employeesData,
    selectedEmployeeId,
    selectedDate,
    selectedDuration,
    bufferAfterMinutes,
    onClose,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const slotsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedDateState) return;
    const isMobileLayout = window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
    if (!isMobileLayout) return;
    const scrollContainer = contentRef.current;
    const slotsEl = slotsRef.current;
    if (!scrollContainer || !slotsEl) return;

    const id = window.setTimeout(() => {
      const offsetTop = slotsEl.offsetTop - scrollContainer.offsetTop;
      scrollContainer.scrollTo({
        top: Math.max(0, offsetTop - 48),
        behavior: "smooth",
      });
    }, 80);

    return () => window.clearTimeout(id);
  }, [selectedDateState]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.header_top}>
          <div className={styles.header_title}>Резервация на час</div>
          <div className={styles.header_subtitle}>
            Изберете служител, дата и час
          </div>
        </div>
        <EmployeeChipStrip
          employees={employeesData}
          activeEmployeeId={currentEmployee}
          onSelect={handleEmployeeChange}
        />
      </div>

      <div className={styles.content} ref={contentRef}>
        <div className={styles.content_calendar}>
          <CalendarPanel
            monthYearText={monthYearText}
            calendarDays={calendarDays}
            canNavigatePrev={canNavigatePrev}
            onNavigateMonth={navigateMonth}
            onDateSelect={handleDateSelect}
          />
        </div>
        <div className={styles.content_slots} ref={slotsRef}>
          <SlotsPanel
            hasEmployee={!!selectedEmployee}
            hasDate={!!selectedDateState}
            selectedDateText={selectedDateText}
            groupedSlots={groupedSlots}
            totalSlots={timeSlots.length}
            onSelectSlot={handleTimeSlotSelect}
            selectionMode={selectionMode}
            customDateTime={customDateTime}
            customConflictWarning={customConflictWarning}
            onOpenCustomMode={openCustomMode}
            onCloseCustomMode={closeCustomMode}
            onCustomDateTimeChange={handleCustomDateTimeChange}
          />
        </div>
      </div>

      <SummaryFooter
        employeeName={summary.employeeName}
        dateTime={summary.dateTime}
        canConfirm={canConfirm}
        onConfirm={handleConfirm}
        onCancel={onClose}
      />
    </div>
  );
};
